/**
 * The thread's message queue, Cursor-style: messages typed while a turn runs,
 * stacked above the input as cards (each the user bubble it will become) in
 * the order they will be sent. Drag to reorder, click the text to edit, ↑ to
 * send one into the running turn now, × to drop it. Lives on the thread, so
 * teammates see (and can reorder) the same list. Case Prep steps waiting for
 * the thread list after it: they keep their skill, which the thread's queue
 * can't carry, so they stay in this browser and send themselves in order.
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowUp, Loader2, Paperclip, X } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type FC,
  type KeyboardEvent
} from "react";

import {
  Queue,
  QueueItem,
  QueueItemAction,
  QueueItemActions,
  QueueItemContent,
  QueueItemIndicator,
  QueueSection,
  QueueSectionContent,
  QueueSectionLabel,
  QueueSectionTrigger
} from "@/components/ai-elements/queue";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { QueuedMessage, SteerItem } from "../api/agentApi";

type RowProps = {
  item: QueuedMessage;
  mine: boolean;
  editing: boolean;
  onStartEdit: () => void;
  /** `null` cancels. */
  onEndEdit: (text: string | null) => void;
  onRemove: () => void;
  onSendNow: () => void;
};

const Row: FC<RowProps> = ({
  item,
  mine,
  editing,
  onStartEdit,
  onEndEdit,
  onRemove,
  onSendNow
}) => {
  const pending = item.id.startsWith("pending_");
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item.id, disabled: editing || pending });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [draft, setDraft] = useState(item.text);
  useEffect(() => {
    if (editing) {
      setDraft(item.text);
      const el = inputRef.current;
      el?.focus();
      el?.setSelectionRange(el.value.length, el.value.length);
    }
  }, [editing, item.text]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onEndEdit(draft);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEndEdit(null);
    }
  };
  const editable = mine && !pending;
  // A queued Build shows its plan, not the prompt; it cannot be reworded or
  // sent into the running turn (it starts its plan as a turn of its own).
  const build = item.build ?? null;
  const editableText = editable && !build;

  return (
    // Cursor's row: a hollow circle, then the text at the chat size in the
    // foreground colour. The circle is the drag handle; ↑ and × show on hover.
    <QueueItem
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative flex-row items-start gap-2 rounded-md px-2 py-1 text-base leading-5 text-foreground hover:bg-fill-1",
        isDragging && "bg-muted opacity-80 shadow-sm",
        pending && "opacity-60"
      )}
      data-testid="composer-queued-item"
      data-queued-id={item.id}
      {...attributes}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className={cn(
          "mt-1 flex size-3.5 shrink-0 cursor-grab items-center justify-center rounded-full",
          pending && "cursor-default"
        )}
        aria-label="Drag to reorder"
        data-testid="composer-queued-handle"
        {...listeners}
      >
        <QueueItemIndicator className="mt-0 size-3.5 border-muted-foreground/60" />
      </button>
      {editing ? (
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => onEndEdit(draft)}
          rows={Math.min(6, Math.max(1, draft.split("\n").length))}
          className="min-w-0 flex-1 resize-none bg-transparent p-0 text-base leading-5 text-foreground outline-none"
          aria-label="Edit queued message"
          data-testid="composer-queued-edit"
        />
      ) : (
        <QueueItemContent
          className={cn(
            "line-clamp-none min-w-0 flex-1 whitespace-pre-wrap break-words text-foreground",
            editableText && "cursor-text",
            // Room for the hover actions so they never cover the last words.
            editable && "pr-12"
          )}
          title={
            build
              ? build.title
              : mine
              ? "Click to edit"
              : `${item.name} queued this`
          }
          onClick={editableText ? onStartEdit : undefined}
        >
          {!mine ? (
            <span className="mr-1 font-medium text-muted-foreground">
              {item.name}:
            </span>
          ) : null}
          {build ? (
            <span data-testid="queued-build">
              <span className="text-muted-foreground">Build · </span>
              {build.title}
            </span>
          ) : (
            item.text
          )}
          {item.attachments?.length ? (
            <span
              className="ml-1 inline-flex items-center gap-0.5 text-muted-foreground"
              title={item.attachments
                .map((a) => a.filename ?? "file")
                .join(", ")}
              data-testid="queued-attachments"
            >
              <Paperclip className="size-3" />
              {item.attachments.length}
            </span>
          ) : null}
        </QueueItemContent>
      )}
      {!editing && editable ? (
        <QueueItemActions className="absolute top-0.5 right-1 items-center gap-0.5">
          {!build ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <QueueItemAction
                  aria-label="Send now"
                  onClick={onSendNow}
                  className="size-6 justify-center rounded-md p-0 hover:bg-background"
                  data-testid="composer-queued-send-now"
                >
                  <ArrowUp className="size-3.5" />
                </QueueItemAction>
              </TooltipTrigger>
              <TooltipContent side="top">Send now</TooltipContent>
            </Tooltip>
          ) : null}
          <Tooltip>
            <TooltipTrigger asChild>
              <QueueItemAction
                aria-label="Remove from queue"
                onClick={onRemove}
                className="size-6 justify-center rounded-md p-0 hover:bg-background"
                data-testid="composer-queued-remove"
              >
                <X className="size-3.5" />
              </QueueItemAction>
            </TooltipTrigger>
            <TooltipContent side="top">Remove</TooltipContent>
          </Tooltip>
        </QueueItemActions>
      ) : null}
    </QueueItem>
  );
};

/** A Case Prep step that sends once the thread is free. */
export type QueuedWorkflow = { id: string; text: string };

const WorkflowRow: FC<{ item: QueuedWorkflow; onRemove: () => void }> = ({
  item,
  onRemove
}) => (
  <QueueItem
    className="relative flex-row items-start gap-2 rounded-md px-2 py-1 text-base leading-5 text-foreground hover:bg-fill-1"
    data-testid="composer-queued-workflow"
    data-workflow-id={item.id}
  >
    <span className="mt-1 flex size-3.5 shrink-0 items-center justify-center">
      <QueueItemIndicator className="mt-0 size-3.5 border-muted-foreground/60" />
    </span>
    <QueueItemContent className="line-clamp-none min-w-0 flex-1 whitespace-pre-wrap break-words pr-12 text-foreground">
      <span className="text-muted-foreground">Case Prep · </span>
      {item.text}
    </QueueItemContent>
    <QueueItemActions className="absolute top-0.5 right-1 items-center gap-0.5">
      <Tooltip>
        <TooltipTrigger asChild>
          <QueueItemAction
            aria-label="Remove from queue"
            onClick={onRemove}
            className="size-6 justify-center rounded-md p-0 hover:bg-background"
            data-testid="composer-queued-workflow-remove"
          >
            <X className="size-3.5" />
          </QueueItemAction>
        </TooltipTrigger>
        <TooltipContent side="top">Remove</TooltipContent>
      </Tooltip>
    </QueueItemActions>
  </QueueItem>
);

type Props = {
  items: QueuedMessage[];
  /** Case Prep steps waiting on this thread, after `items`. */
  workflows?: QueuedWorkflow[];
  onRemoveWorkflow?: (id: string) => void;
  /** "Send now" messages the running turn has not picked up yet. */
  steering?: SteerItem[];
  /** After Stop: nothing is sent until the person resumes or picks one. */
  paused?: boolean;
  /** The signed-in user; only their own messages can be edited or removed. */
  meUid?: string | null;
  onEdit: (id: string, text: string) => void;
  onRemove: (id: string) => void;
  onReorder: (ids: string[]) => void;
  onSendNow: (id: string) => void;
  onResume?: () => void;
  className?: string;
};

export const QueuedMessages: FC<Props> = ({
  items,
  workflows = [],
  onRemoveWorkflow,
  steering = [],
  paused = false,
  meUid = null,
  onEdit,
  onRemove,
  onReorder,
  onSendNow,
  onResume,
  className
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const ids = items.map((q) => q.id);
  const [editing, setEditing] = useState<string | null>(null);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(ids, from, to));
  };

  const count = items.length + workflows.length;
  if (!count && !steering.length) return null;

  return (
    // Cursor's queue panel: a bordered surface the width of the composer
    // that tucks under the box (the box paints over its bottom edge), with a
    // collapsible "N Queued Messages" header and circle-indicator rows.
    <Queue
      className={cn(
        "-mb-2 gap-0 rounded-lg border-line-2 bg-fill-1 px-1.5 pt-1 pb-3.5 shadow-none",
        className
      )}
      data-testid="composer-queue-list"
      data-paused={paused || undefined}
    >
      {steering.length ? (
        <ul className="flex flex-col" data-testid="composer-steering-list">
          {steering.map((s) => (
            <li
              key={s.id}
              className="flex items-start gap-2 rounded-md px-2 py-1 text-base leading-5 text-foreground"
              data-testid="composer-steering-item"
            >
              <Loader2
                className="mt-1 size-3.5 shrink-0 animate-spin text-muted-foreground"
                aria-label="Sending now"
              />
              <span className="min-w-0 flex-1 whitespace-pre-wrap break-words">
                {s.text}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {count ? (
        <QueueSection>
          <div className="flex items-center gap-1">
            <QueueSectionTrigger
              className="h-7 flex-1 rounded-md bg-transparent px-2 py-0 text-base font-normal text-muted-foreground hover:bg-fill-1 hover:text-foreground"
              data-testid="composer-queue-header"
            >
              <QueueSectionLabel
                count={count}
                label={count === 1 ? "Queued Message" : "Queued Messages"}
                className="gap-1.5 [&>svg]:size-3.5"
              />
            </QueueSectionTrigger>
            {paused ? (
              <div
                className="flex shrink-0 items-center gap-1 pr-1 text-sm text-muted-foreground"
                data-testid="composer-queue-paused"
              >
                Queue paused
                {onResume ? (
                  <>
                    <span aria-hidden>·</span>
                    <Button
                      type="button"
                      variant="link"
                      size="xs"
                      className="h-auto p-0 text-sm text-fg-2 underline-offset-2 hover:text-foreground"
                      onClick={onResume}
                      data-testid="composer-queue-resume"
                    >
                      Resume
                    </Button>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          <QueueSectionContent>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={ids}
                strategy={verticalListSortingStrategy}
              >
                <ul
                  className="flex max-h-48 flex-col overflow-y-auto"
                  data-testid="composer-queued-rows"
                >
                  {items.map((item) => (
                    <Row
                      key={item.id}
                      item={item}
                      mine={!meUid || item.uid === meUid}
                      editing={editing === item.id}
                      onStartEdit={() => setEditing(item.id)}
                      onEndEdit={(text) => {
                        setEditing(null);
                        if (text !== null && text.trim() && text !== item.text)
                          onEdit(item.id, text.trim());
                      }}
                      onRemove={() => onRemove(item.id)}
                      onSendNow={() => onSendNow(item.id)}
                    />
                  ))}
                  {workflows.map((item) => (
                    <WorkflowRow
                      key={item.id}
                      item={item}
                      onRemove={() => onRemoveWorkflow?.(item.id)}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          </QueueSectionContent>
        </QueueSection>
      ) : null}
    </Queue>
  );
};
